import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import type { MemoizedFn } from 'memoize-one';
import memoizeOne from 'memoize-one';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { liftPromiseState } from '@confluence/lifted-promise';
import { BadStatusError } from '@confluence/network';
import { getLogger } from '@confluence/logger';

import { onScriptsAndCSSLoaded } from './onScriptsAndCSSLoaded.ts';
import { overrideAJSMeta } from './overrideAjsMeta.ts';
import { waitForWRM } from './waitForWRM.ts';
import { validateSuperbatchResources } from './validateSuperbatchResources.ts';
import {
	isSuperbatchRecoveryEnabled,
	withSuperbatchRecoveryTimeout,
} from './superbatchRecovery.ts';
import type {
	requireSuperBatchFrontendResourceRenderQuery$data,
	requireSuperBatchFrontendResourceRenderQuery$variables,
} from './__generated__/requireSuperBatchFrontendResourceRenderQuery.graphql.ts';

type ResourceType = 'js' | 'css';

type Resource = {
	type: ResourceType;
	attributes: { [key: string]: string };
	url: string;
};
type ResourceByType = Record<ResourceType, Omit<Resource, 'type'>[]>;

export const requireSuperBatchFrontendResourceRenderQuery: GraphQLTaggedNode = graphql`
	query requireSuperBatchFrontendResourceRenderQuery {
		internalFrontendResource {
			resourceList {
				type
				url
				attributes {
					key
					value
				}
			}
		}
	}
`;

const getSuperBatchData = memoizeOne(async (): Promise<Resource[]> => {
	return queryInterop<
		requireSuperBatchFrontendResourceRenderQuery$data,
		requireSuperBatchFrontendResourceRenderQuery$variables
	>({
		query: requireSuperBatchFrontendResourceRenderQuery,
		context: {
			allowOnExternalPage: true,
		},
	}).then(
		(response) => {
			if (isSuperbatchRecoveryEnabled()) {
				// queryInterop exposes GraphQL errors as `error`, rather than the raw `errors` array.
				const resources = validateSuperbatchResources({
					data: response.data,
					errors: response.error ? [response.error] : [],
				});
				// Relay results can be frozen. Normalize into fresh records rather than mutating the cache.
				return resources.map(({ type, url, attributes }) => ({
					type,
					url,
					attributes: Object.fromEntries(attributes.map(({ key, value }) => [key, value] as const)),
				}));
			}
			return (
				(response?.data?.internalFrontendResource?.resourceList || []).map((resource: any) => {
					resource.attributes = resource.attributes || {};

					if (Array.isArray(resource.attributes)) {
						resource.attributes = resource.attributes.reduce((obj: any, { key, value }: any) => {
							obj[key] = value;
							return obj;
						}, {});
					}

					return resource;
				}) || []
			);
		},
		(reason: any) => {
			if (reason instanceof BadStatusError && !isSuperbatchRecoveryEnabled()) {
				return [];
			}

			throw reason;
		},
	);
});

function createElement<K extends keyof HTMLElementTagNameMap>(
	type: K,
	attributes: Record<string, string>,
): HTMLElementTagNameMap[K] {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const element = document.createElement(type);
	Object.keys(attributes).forEach((attributeKey) =>
		element.setAttribute(attributeKey, attributes[attributeKey]),
	);
	return element;
}

function createStyleElement(attributes: Record<string, string>): HTMLLinkElement {
	return createElement('link', { rel: 'stylesheet', ...attributes });
}

function createSyncScriptElement(attributes: Record<string, string>): HTMLScriptElement {
	attributes.crossorigin = 'anonymous';
	const element = createElement('script', attributes);
	element.async = false;
	return element;
}

export function getResourcesByType(resourceList: Resource[]): ResourceByType {
	return resourceList.reduce(
		(resources, { type, attributes, url }) => {
			if (!resources[type]) {
				resources[type] = [];
			}
			resources[type].push({ attributes, url });
			return resources;
		},
		{ js: [], css: [] } as ResourceByType,
	);
}

/**
 * Fix for HOT-88486 monolith is incorrectly returning hash. Change it to local.
 * TODO: Remove when BE fixed it.
 * export for test
 */
export function replaceHash(url: string): string {
	return url.replace(/externals=[^&]+&/, 'externals=__local-default__&');
}

function normalizeResourceURL(url: string): string {
	// SSR HTML decodes entities, while the GraphQL resource URL can still contain them.
	const normalized = replaceHash(url.replace(/&amp;/g, '&'));
	try {
		return new URL(normalized, window.document.baseURI).href;
	} catch {
		return normalized;
	}
}

function isResourceAlreadyInserted(
	type: ResourceType,
	url: string,
	wrmKey: string | undefined,
	fragment: DocumentFragment,
): boolean {
	const selector = type === 'js' ? 'script' : 'link[rel="stylesheet"]';
	const urlAttribute = type === 'js' ? 'src' : 'href';
	const normalizedURL = normalizeResourceURL(url);
	// Include the pending fragment: duplicate entries are processed before it enters the document.
	return [window.document, fragment].some((container) =>
		Array.from(container.querySelectorAll(selector)).some((tag) => {
			// A new WRM resource can contain several files sharing a key; keep their distinct URLs.
			if (
				container === window.document &&
				wrmKey !== undefined &&
				tag.getAttribute('data-wrm-key') === wrmKey
			) {
				return true;
			}
			const existingURL = tag.getAttribute(urlAttribute);
			return existingURL !== null && normalizeResourceURL(existingURL) === normalizedURL;
		}),
	);
}

async function fetchAndInsertJsAndCssTagsToHead(signal?: AbortSignal): Promise<void | any[]> {
	const resources: Resource[] = await getSuperBatchData();
	if (signal?.aborted) {
		throw new Error('Superbatch loading timed out');
	}
	const resourceByType = getResourcesByType(resources);
	const recoveryEnabled = isSuperbatchRecoveryEnabled();

	const fragment = window.document.createDocumentFragment();
	const tagsAdded: (HTMLScriptElement | HTMLLinkElement)[] = [];
	resourceByType.js.forEach(({ url, attributes }) => {
		// Jquery is always statically loaded in packages/confluence-frontend-server/templates/html/dashboard-spa-container.js
		if (attributes['data-wrm-key'] === 'com.atlassian.plugins.jquery:jquery') {
			return;
		}

		// use https when running locally as some links with the
		// non-secure http protocol throws 403 locally instead of redirecting to https
		const src =
			process.env.NODE_ENV === 'development' ? `https:${replaceHash(url)}` : replaceHash(url);
		const alreadyInserted = recoveryEnabled
			? isResourceAlreadyInserted('js', src, attributes['data-wrm-key'], fragment)
			: Boolean(
					window.document.querySelector(`script[data-wrm-key='${attributes['data-wrm-key']}']`),
				);
		if (!alreadyInserted) {
			const tag = createSyncScriptElement({
				// Note: url on BE pipeline is relative
				src,
				...attributes,
			});
			tagsAdded.push(tag);
			fragment.appendChild(tag);
		}
	});
	resourceByType.css.forEach(({ url, attributes }) => {
		const src =
			process.env.NODE_ENV === 'development' ? `https:${replaceHash(url)}` : replaceHash(url);
		const alreadyInserted = recoveryEnabled
			? isResourceAlreadyInserted('css', src, attributes['data-wrm-key'], fragment)
			: Boolean(
					window.document.querySelector(`link[data-wrm-key='${attributes['data-wrm-key']}']`),
				);
		if (!alreadyInserted) {
			const tag = createStyleElement({
				// Note: url on BE pipeline is relative
				href: src,
				...attributes,
			});
			tagsAdded.push(tag);
			fragment.appendChild(tag);
		}
	});

	if (tagsAdded.length) {
		if (recoveryEnabled) {
			// Attach handlers before insertion, including for cached scripts that load immediately.
			const loaded = onScriptsAndCSSLoaded(tagsAdded, signal);
			window.document.head.appendChild(fragment);
			return loaded;
		}
		window.document.head.appendChild(fragment);
		return onScriptsAndCSSLoaded(tagsAdded);
	}

	return Promise.resolve();
}

export const requireSuperBatch: MemoizedFn<() => Promise<void>> = memoizeOne((): Promise<void> => {
	// Server-emitted tags avoid a duplicate fetch. Treatment also checks WRM readiness
	// because an emitted asynchronous script may still be loading or may have failed.
	if (isSuperbatchRecoveryEnabled()) {
		return liftPromiseState(
			withSuperbatchRecoveryTimeout(async (signal) => {
				if (!window.__SUPERBATCH_REQUESTED__) {
					await fetchAndInsertJsAndCssTagsToHead(signal);
				}
				await waitForWRM(signal);
				if (signal.aborted) {
					throw new Error('Superbatch loading timed out');
				}
				overrideAJSMeta();
			}).catch((error: unknown) => {
				getLogger('wrm').warn`Superbatch recovery failed`;
				throw error;
			}),
		);
	}
	const superbatchPromise = window.__SUPERBATCH_REQUESTED__
		? Promise.resolve()
		: fetchAndInsertJsAndCssTagsToHead();

	return liftPromiseState(superbatchPromise.then(() => overrideAJSMeta()));
});
